import * as XLSX from "xlsx";

export interface TableSchemaInfo {
  table_name: string;
  columns: { column_name: string }[];
}

export interface TableSuggestion {
  tableName: string;
  score: number;
  matchedColumns: number;
  totalFileHeaders: number;
}

/**
 * Normalize a column/header name for comparison.
 * Strips non-alphanumeric chars, lowercases, collapses underscores.
 */
function normalize(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/**
 * Suggest the best matching table based on file header columns vs table columns.
 * Returns the best match with score, or null if no good match found.
 */
export function suggestTableByHeaders(
  fileHeaders: string[],
  schema: TableSchemaInfo[]
): TableSuggestion | null {
  if (fileHeaders.length === 0 || schema.length === 0) return null;

  const normalizedHeaders = fileHeaders.map(normalize).filter((h) => h.length > 0);
  if (normalizedHeaders.length === 0) return null;

  let best: TableSuggestion | null = null;

  for (const table of schema) {
    const tableColSet = new Set(table.columns.map((c) => normalize(c.column_name)));
    const matched = normalizedHeaders.filter((h) => tableColSet.has(h)).length;
    const score = matched / normalizedHeaders.length;

    if (matched >= 2 && (!best || score > best.score || (score === best.score && matched > best.matchedColumns))) {
      best = {
        tableName: table.table_name,
        score,
        matchedColumns: matched,
        totalFileHeaders: normalizedHeaders.length,
      };
    }
  }

  return best;
}

/**
 * Suggest a table based on file name similarity to existing table names.
 */
export function suggestTableByFileName(
  fileName: string,
  tables: string[]
): string | null {
  if (!fileName || tables.length === 0) return null;

  const baseName = normalize(fileName.replace(/\.[^.]+$/, ""));
  if (!baseName) return null;

  // Exact match
  const exact = tables.find((t) => normalize(t) === baseName);
  if (exact) return exact;

  // Table name contained in file name or vice versa
  let bestMatch: string | null = null;
  let bestOverlap = 0;

  for (const t of tables) {
    const nt = normalize(t);
    if (baseName.includes(nt) || nt.includes(baseName)) {
      const overlap = Math.min(nt.length, baseName.length);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestMatch = t;
      }
    }
  }

  return bestMatch;
}

/**
 * Parse the header row from a CSV string.
 */
export function parseCSVHeaders(text: string): string[] {
  const firstLine = text.split(/\r?\n/)[0];
  if (!firstLine) return [];

  const headers: string[] = [];
  let current = "";
  let inQuotes = false;

  for (const char of firstLine) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      headers.push(current.trim().replace(/^"|"$/g, ""));
      current = "";
    } else {
      current += char;
    }
  }
  headers.push(current.trim().replace(/^"|"$/g, ""));

  return headers.filter((h) => h.length > 0);
}

/**
 * Parse the header row from an Excel file (ArrayBuffer).
 * Reads the first row of the first sheet.
 */
export function parseExcelHeaders(buffer: ArrayBuffer): string[] {
  try {
    const workbook = XLSX.read(buffer, { type: "array", sheetRows: 1 });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];

    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
    if (!rows[0] || !Array.isArray(rows[0])) return [];

    return rows[0].map((v) => String(v ?? "").trim()).filter((h) => h.length > 0);
  } catch {
    return [];
  }
}

/**
 * Extract headers from a file Blob based on its type.
 */
export async function extractFileHeaders(
  file: Blob,
  fileName: string
): Promise<string[]> {
  const ext = fileName.split(".").pop()?.toLowerCase();

  if (ext === "csv") {
    // Read just the first 4KB for the header line
    const slice = file.slice(0, 4096);
    const text = await slice.text();
    return parseCSVHeaders(text);
  }

  if (ext === "xlsx" || ext === "xls") {
    const buffer = await file.arrayBuffer();
    return parseExcelHeaders(buffer);
  }

  return [];
}
